import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Check, PenLine } from "lucide-react";
import { api } from "../../lib/api";

const inputCls =
  "w-full bg-obsidian/60 border border-woodborder focus:border-burnt text-cream text-sm px-5 py-4 outline-none transition-colors placeholder:text-clay rounded-sm";
const labelCls = "block font-mono text-[10px] tracking-[0.22em] text-tan mb-2";

export default function ContributeWriter({ variant = "inline" }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", website: "", social: "", interests: "", experience: "", message: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (form.name.trim().length < 2) errs.name = "Name required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Valid email required.";
    if (form.message.trim().length < 5) errs.message = "Say a few words.";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setStatus("loading");
    try {
      await api.sendInquiry({ type: "writer", ...form });
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  return (
    <>
      {variant === "inline" ? (
        <button onClick={() => setOpen(true)} data-testid="contribute-open" className="group flex items-center gap-2 font-mono text-[11px] tracking-[0.22em] text-tan hover:text-gold-bright transition-colors">
          <PenLine size={13} /> BECOME A CONTRIBUTING WRITER <span className="text-burnt">→</span>
        </button>
      ) : (
        <button onClick={() => setOpen(true)} data-testid="contribute-open-footer" className="font-mono text-[10px] tracking-[0.22em] text-clay hover:text-tan transition-colors">
          WRITE FOR THE REPORT
        </button>
      )}

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] flex items-start justify-center p-4 overflow-y-auto"
            data-testid="contribute-modal"
          >
            <div className="absolute inset-0 bg-obsidian/85 backdrop-blur-sm" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative bg-wood border border-woodborder w-full max-w-xl my-8"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-woodborder">
                <h3 className="font-display text-2xl text-cream tracking-wide">WRITE FOR THE REPORT</h3>
                <button onClick={() => setOpen(false)} className="text-sand hover:text-cream p-1" data-testid="cw-close" aria-label="Close">
                  <X size={20} />
                </button>
              </div>
              <div className="p-6 max-h-[75vh] overflow-y-auto">
                {status === "success" ? (
                  <div className="py-10 text-center" data-testid="contribute-success">
                    <span className="mx-auto w-14 h-14 rounded-full bg-gold flex items-center justify-center">
                      <Check size={24} className="text-obsidian" />
                    </span>
                    <h4 className="mt-6 font-display text-3xl text-cream tracking-wide">PITCH RECEIVED.</h4>
                    <p className="mt-3 text-sand font-light max-w-xs mx-auto text-sm">The editorial desk reviews every pitch. If it fits the culture, you'll hear from us.</p>
                  </div>
                ) : (
                  <form onSubmit={submit} className="space-y-5" data-testid="contribute-form" noValidate>
                    <p className="text-sand text-sm font-light">Got a voice the culture needs to hear? Pitch the editorial desk.</p>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className={labelCls}>NAME *</label>
                        <input className={inputCls} value={form.name} onChange={set("name")} data-testid="cw-name-input" />
                        {errors.name && <p className="mt-1 font-mono text-[10px] text-burnt-hover">{errors.name}</p>}
                      </div>
                      <div>
                        <label className={labelCls}>EMAIL *</label>
                        <input type="email" className={inputCls} value={form.email} onChange={set("email")} data-testid="cw-email-input" />
                        {errors.email && <p className="mt-1 font-mono text-[10px] text-burnt-hover">{errors.email}</p>}
                      </div>
                      <div>
                        <label className={labelCls}>PORTFOLIO / WEBSITE</label>
                        <input className={inputCls} value={form.website} onChange={set("website")} placeholder="https://" data-testid="cw-website-input" />
                      </div>
                      <div>
                        <label className={labelCls}>SOCIAL</label>
                        <input className={inputCls} value={form.social} onChange={set("social")} placeholder="@yourhandle" data-testid="cw-social-input" />
                      </div>
                    </div>
                    <div>
                      <label className={labelCls}>AREAS OF INTEREST</label>
                      <input className={inputCls} value={form.interests} onChange={set("interests")} placeholder="Music, culture, business, style..." data-testid="cw-interests-input" />
                    </div>
                    <div>
                      <label className={labelCls}>WRITING SAMPLE OR RELEVANT EXPERIENCE</label>
                      <textarea className={`${inputCls} min-h-[90px] resize-y`} value={form.experience} onChange={set("experience")} data-testid="cw-experience-textarea" />
                    </div>
                    <div>
                      <label className={labelCls}>SHORT MESSAGE *</label>
                      <textarea className={`${inputCls} min-h-[80px] resize-y`} value={form.message} onChange={set("message")} placeholder="Why you, why now" data-testid="cw-message-textarea" />
                      {errors.message && <p className="mt-1 font-mono text-[10px] text-burnt-hover">{errors.message}</p>}
                    </div>
                    {status === "error" && <p className="font-mono text-[11px] text-burnt-hover" data-testid="cw-form-error">Submission glitched. Try again.</p>}
                    <button
                      type="submit" disabled={status === "loading"}
                      className="w-full bg-burnt hover:bg-burnt-hover text-cream font-mono text-xs tracking-[0.25em] px-8 py-4 rounded-full transition-colors disabled:opacity-60"
                      data-testid="cw-submit"
                    >
                      {status === "loading" ? "SENDING..." : "SUBMIT PITCH"}
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
